import React, { useState } from 'react';
import {
  Confirmation,
  Drawer,
  Loader,
  MenuItemProps,
  Table,
  Tooltip,
  useToasts,
} from 'freemium-ui';
import { useLocalStorage } from 'hooks';
import { get, isEmpty, isNil, uniq } from 'lodash';
import moment from 'moment-timezone';
import qs from 'query-string';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import FilterViewPopOvers from 'routes/screens/components/CustomFilterView/popovers';
import {
  useDeleteFilterView,
  useGetFilterView,
  useGetFilterViews,
  useSaveFilterView,
} from 'routes/screens/contacts/components/FilterViews/hooks';
import { useGetContactLists } from 'routes/screens/lists/hooks';
import { SelectAllContainer } from 'routes/styles';
import EmptyState from 'shared/components/EmptyState';
import Icon from 'shared/components/Icon';
import Spacer from 'shared/components/Spacer';
import { useGetSurvey } from 'shared/hooks';
import { Center, FreshLink } from 'shared/styles';
import {
  AccountInfoType,
  ConditionType,
  ConfirmationType,
  FilterViewList,
  FilterViewType,
  RangeLabelType,
  SurveyQuestionType,
  SurveyResponseType,
  SurveysResponsesResponseType,
  UserType,
} from 'shared/types';
import { getAccountTimeFormat, getChannelName } from 'shared/utils';
import { useAccessControl } from 'utils/access-control';
import FilterContent from './components/filter';
import {
  ResponseFields,
  responseColumnToExclude,
} from './components/filter/constants';
import {
  constructFilterURL,
  constructLocalStateFilters,
} from './components/filter/utils';
import ResponseListHeader from './components/response-header';
import ResponseDrawer from './components/response_drawer';
import {
  useDeleteResponseMutation,
  useGetResponseCount,
  useGetResponses,
  useGetSurveys,
} from '../../hooks';
import { getRatingIcon } from '../../utils';
import {
  checkFilterConditionsModified,
  getAllFiltersFromFV,
  getIconGlyph,
  getPayloadForFilterView,
} from './utils';
import { RESPONSE_PER_PAGE } from '../../constants';
import { ResponseTable } from '../../style';
import { ValueBox } from './style';

export type ViewProps = {
  accountInfo?: AccountInfoType;
  userInfo?: UserType;
  setConfirmationData?: (
    str: ConfirmationType | React.SetStateAction<ConfirmationType>,
  ) => void;
};

export type AppliedFilterType = {
  filterParams: string;
  localState: any;
  isApply: boolean;
};

function ResponseList({
  accountInfo,
  userInfo,
  setConfirmationData,
}: ViewProps) {
  const { t } = useTranslation();
  const params = useParams<{
    id: string;
  }>();
  const { access = [] } = useAccessControl();
  const queryClient = useQueryClient();
  const history = useHistory();
  const { state: locationState, search: searchParams } = useLocation();
  const [, setToast] = useToasts();

  const [currentSurvey, setCurrentSurvey] = useState<string>(params?.id);
  const [selectedFilterView, setselectedFilterView] =
    useState<FilterViewType | null>(
      (locationState as any)?.selectedFilter ?? null,
    );
  const [appliedFilters, setAppliedFilters] = useLocalStorage(
    'response:filter',
    {
      filterParams: '',
      localState: {},
      isApply: false,
    },
  );
  const [selectedFilterViewId, setSelectedFilterViewId] = useLocalStorage(
    'response:filterViewId',
    null,
  );
  const [deleteResponseId, setDeleteResponseId] = useState<string | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);
  const [surveyOpen, setSurveyOpen] = useState<boolean>(false);
  const [allresponses, setAllresponses] = useState<SurveyResponseType[]>();
  const [isFilterConditionsModified, setisFilterConditionsModified] =
    useState<boolean>(false);
  const [pageIndex, setPageIndex] = React.useState<number>(0);
  const [selectedRowIds, setSelectedRowIds] = React.useState<
    Record<string, boolean>
  >({});
  const [isAllSelected, setAllSelected] = React.useState<boolean>(false);
  const [pageToken, setPageToken] = React.useState<Record<number, string>>({
    0: '',
  });
  const [, setIsResponsesDeleted] = useState<Record<string, boolean>>({
    [currentSurvey]: false,
  });
  const [filterViewEditAnchorEl, setFilterViewEditAnchorEl] =
    React.useState<HTMLDivElement | null>(null);

  const [filterViewName, setFilterViewName] = React.useState<string>('');
  const [anchorEl, setAnchorEl] = React.useState<any>(null);

  const { data: surveyData = [], isFetching: isDefaultSurveyFetching } =
    useGetSurveys(!params?.id);
  const { data: surveyInfo, isLoading: isLoadingSurveyInfo } = useGetSurvey({
    surveyId: currentSurvey || params?.id,
  });

  const { data: listsData = {} } = useGetContactLists({
    refetchOnWindowFocus: true,
  });

  const ratingQuestion = React.useMemo(
    () =>
      surveyInfo?.question_details?.questions?.find(
        (que: SurveyQuestionType) => !!que.type_info?.pivot_question,
      ),
    [surveyInfo],
  );

  const {
    data: totalCount,
    refetch: refetchTotalCount,
    isLoading: isCountLoading,
  } = useGetResponseCount({
    id: currentSurvey,
    ratingQuestion: ratingQuestion || {},
    currentSurvey,
    responseFilterCriteria: appliedFilters,
  });

  const {
    data: responseData,
    isLoading,
    isFetching,
    refetch: refetchResponses,
  } = useGetResponses({
    surveyId: currentSurvey,
    appliedFilters,
    pageToken: pageToken[pageIndex] || '',
    callback: async (
      isSuccess: boolean,
      response: SurveysResponsesResponseType,
    ) => {
      if (isSuccess) {
        setAllresponses(response?.surveyResponses || []);
        const nextURL = get(response, ['_links', 'next', 'href'], '');
        if (nextURL) {
          const parseData = qs.parseUrl(nextURL);
          if (parseData.query?.next_token) {
            setPageToken((pgTkn) => ({
              ...pgTkn,
              [pageIndex + 1]: parseData.query.next_token as string,
            }));
          }
        }
      }
    },
  });

  const { mutate: onSaveFilterView } = useSaveFilterView({
    setToast,
    callback: (
      isSuccess: boolean,
      response: FilterViewType,
      isCreated: boolean,
    ) => {
      if (isSuccess) {
        if (isCreated) {
          queryClient.invalidateQueries('filters-response');
        } else {
          queryClient.invalidateQueries(`filterView-${response.id}`);
        }
        onDefaultFilterViewChange({
          ...response,
          conditions: Object.values(response?.conditions),
        });
      }
    },
    filter_view_type: 'RESPONSE',
  });

  const { data: allFilterViews } = useGetFilterViews({
    callback: (isSuccess: boolean, response: FilterViewList) => {
      if (isSuccess) {
        response?.filter_view_list?.map((filter: FilterViewType) => {
          return { ...filter, conditions: filter?.conditions };
        });
      } else {
        setToast({
          type: 'error',
          delay: 3000,
          text: t('empty_state.message.network_error'),
        });
      }
    },
    filter_view_type: 'RESPONSE',
  });

  const { mutate: deleteFilterView } = useDeleteFilterView({
    setToast,
    callback: (isSuccess: boolean) => {
      if (isSuccess) {
        queryClient.invalidateQueries('filters-response');
        if (setConfirmationData) setConfirmationData({});
        setselectedFilterView(null);
        setSelectedFilterViewId(null);
        setisFilterConditionsModified(false);
        setAppliedFilters({
          isApply: true,
          localState: {},
          appliedFilters: '',
        });
        history.push(`/response/${surveyInfo?.id}`);
      }
    },
  });

  const { mutate: deleteResponse } = useDeleteResponseMutation({
    setToast,
    callback: (isSuccess: boolean) => {
      setIsDeleteOpen(false);
      if (isSuccess) setDeleteResponseId(null);
    },
  });

  React.useEffect(() => {
    if (appliedFilters?.isApply) {
      refetchTotalCount();
      refetchResponses();
      setAppliedFilters({
        ...appliedFilters,
        isApply: false,
      });
    }
  }, [appliedFilters, selectedFilterView]);

  React.useEffect(() => {
    const clearCache = new URLSearchParams(searchParams)?.get('clearCache');
    history.push(`/response/${currentSurvey ? currentSurvey : ''}`);
    if (clearCache == 'true') {
      setAppliedFilters({
        filterParams: '',
        localState: {},
        isApply: false,
      });
      setSelectedFilterViewId(null);
    }
  }, [history, searchParams]);

  React.useEffect(() => {
    if (selectedFilterViewId) {
      const data: FilterViewType | undefined = queryClient.getQueryData(
        `filterView-${selectedFilterViewId}`,
      );
      if (data) {
        setselectedFilterView(data);
      } else {
        refetchFilterView();
      }
    }
  }, [selectedFilterViewId]);

  React.useEffect(() => {
    checkFilterModified();
  }, [selectedFilterView, currentSurvey]);

  React.useEffect(() => {
    if (listsData?.contactList && selectedFilterView?.conditions[1]?.value) {
      setAppliedFilters({
        ...appliedFilters,
        isApply: true,
      });
    }
  }, [listsData]);

  React.useEffect(() => {
    if (currentSurvey) refetchResponses();
  }, [pageIndex, currentSurvey]);

  // default route first survey
  React.useEffect(() => {
    if (!currentSurvey && surveyData?.length) {
      setCurrentSurvey(surveyData[0].id as string);
      queryClient.invalidateQueries(`survey-response-${surveyData[0].id}`);
      queryClient.invalidateQueries(`response-count-${surveyData[0].id}`);
    }
  }, [surveyData]);

  React.useEffect(() => {
    if (isEmpty(selectedRowIds)) {
      setAllSelected(false);
    }
  }, [selectedRowIds]);

  const checkFilterModified = (filterView = selectedFilterView) => {
    const isModified = checkFilterConditionsModified(
      appliedFilters?.localState,
      filterView,
      accountInfo,
    );

    if (isModified) {
      setisFilterConditionsModified(true);
    } else {
      setisFilterConditionsModified(false);
    }
  };

  const getAllFilters = (val = selectedFilterView): string => {
    return getAllFiltersFromFV(val, accountInfo);
  };

  const onDefaultFilterViewChange = (val: FilterViewType) => {
    setCurrentSurvey(val.conditions[0].name);
    setselectedFilterView({ ...val, conditions: val.conditions });
    const filters = getAllFilters(val);

    setAppliedFilters({
      filterParams: filters ?? '',
      isApply: true,
      localState: constructLocalStateFilters(
        val.conditions[0]?.value,
        val.conditions[1]?.value,
        listsData?.contactList,
      ),
    });
    setSelectedFilterViewId(val?.id);
    history.push(`/response/${val.conditions[0].name}`);
    setisFilterConditionsModified(false);
  };

  const saveFilterView = (filterViewName, isCreate = false) => {
    const filterView = !isCreate ? selectedFilterView : null;

    const responseConditions: ConditionType[] = getPayloadForFilterView(
      appliedFilters?.localState,
      filterViewName,
      surveyInfo,
      appliedFilters?.filterParams,
    );
    // the ternary condition is for checking whether it is for creating or updating a filter
    onSaveFilterView({
      ...filterView,
      id:
        !filterViewName && !isCreate
          ? selectedFilterView?.id
          : filterView?.id ?? '',
      name: !filterViewName ? selectedFilterView?.name : filterViewName,
      conditions: responseConditions,
    });
  };

  const toggleDrawer = () => {
    setSurveyOpen(!surveyOpen);
  };

  const { refetch: refetchFilterView } = useGetFilterView({
    callback: (isSuccess: boolean, response: FilterViewType) => {
      if (isSuccess) {
        setselectedFilterView(response);
      } else {
        setToast({
          type: 'error',
          delay: 3000,
          text: t('empty_state.message.network_error'),
        });
      }
    },
    filterViewId: selectedFilterViewId,
  });

  const applyFilter = React.useCallback(
    (store: any = null) => {
      const urlConstruct: string = constructFilterURL(
        store || appliedFilters.localState,
        ResponseFields,
        accountInfo,
      );
      setPageIndex(0);
      setPageToken({});
      const filters = {
        localState: store ?? appliedFilters.localState,
        filterParams: urlConstruct,
        isApply: true,
      };
      setAppliedFilters(filters);
      checkFilterModified();
    },
    [appliedFilters],
  );

  const handleReset = () => {
    setPageIndex(0);
    setPageToken({});
    const filters = getAllFilters();
    setAppliedFilters({
      filterParams: filters ?? '',
      localState: selectedFilterView
        ? constructLocalStateFilters(
            selectedFilterView?.conditions[0]?.value,
            selectedFilterView?.conditions[1]?.value,
            listsData?.contactList,
          )
        : {},
      isApply: true,
    });
    setisFilterConditionsModified(false);
  };

  const handleDeleteResponse = () => {
    deleteResponse({
      surveyId: currentSurvey,
      responseId: deleteResponseId || '',
    });
  };

  const selectedRowIdsArray: string[] = React.useMemo(
    () => Object.keys(selectedRowIds),
    [selectedRowIds],
  );

  const count = React.useMemo(() => totalCount?.count || 0, [totalCount]);

  const showSelectionInfo = React.useMemo(() => {
    const currentPageResponses = {};
    allresponses?.forEach((response) => {
      currentPageResponses[response.id || ''] = response;
    });
    return (
      (selectedRowIdsArray.some((i) => !currentPageResponses[i]) ||
        selectedRowIdsArray.length >= RESPONSE_PER_PAGE) &&
      count > RESPONSE_PER_PAGE
    );
  }, [allresponses, selectedRowIdsArray, count]);

  const handleActions = (option: string, id: string) => {
    switch (option) {
      case 'view':
        history.push(`/response/${currentSurvey}/${id}`, {
          selectedFilter: selectedFilterView,
          surveyResponseCount: totalCount?.count || '0',
        });
        break;
      case 'delete':
        setIsDeleteOpen(true);
        setDeleteResponseId(id);
        break;
      default:
        break;
    }
  };

  const onChangeSurvey = (surveyId: string) => {
    history.push(`/response/${surveyId}`);
    window.localStorage.removeItem('response:filter');
  };

  const onDeleteFilterView = React.useCallback(
    (filterId: string, filterName: string) => {
      if (setConfirmationData) {
        setConfirmationData({
          id: new Date().toISOString(),
          title: `${t('forms.confirmation.delete_filter_view')}`,
          className: 'error',
          confirmButtonText: `${t('confirm')}`,
          onConfirm: () => {
            setConfirmationData((old) => ({
              ...old,
              confirmButtonDisabled: true,
            }));
            deleteFilterView(filterId);
          },
          fill: '#d7461f',
          infoText: `${t('forms.confirmation.action_irreversible')}`,
          description: `${t('forms.confirmation.confirm_filter_view_delete', {
            filterName: filterName,
          })}`,
          glyph: 'info',
        });
      }
    },
    [],
  );

  const clearFilterView = (selectedFilter) => {
    const filters = getAllFilters(selectedFilter);
    setAppliedFilters({
      filterParams: filters ?? '',
      isApply: true,
      localState: constructLocalStateFilters(
        selectedFilter?.conditions[0]?.value,
        selectedFilter?.conditions[1]?.value,
        listsData?.contactList,
      ),
    });
    setisFilterConditionsModified(false);
  };

  const columns = [
    {
      id: 'Respondent',
      Header: `${t('responses.table_header.respondent')}`,
      accessor: ({
        contact_first_name,
        contact_last_name,
        contact_email,
        channel,
        id,
        contact_id,
      }: SurveyResponseType) => {
        const displayName =
          contact_first_name || contact_last_name
            ? `${contact_first_name || ''} ${contact_last_name || ''}`
            : contact_email
            ? contact_email
            : contact_id
            ? `${t('deleted_contact')}`
            : `${t('anonymous')}`;

        return (
          <div className="recipient">
            <div className="name">
              <div className="contact-name">{displayName}</div>
              <div className="contact-id">#RES{id}</div>
            </div>
            <div className="channel">
              <Icon
                glyph={getIconGlyph(channel || 'EMAIL')}
                width={14}
                height={14}
                fill="#12344d"
              />
              <Spacer x={0.3} />

              <div>
                {' '}
                {channel && getChannelName(channel)
                  ? t(getChannelName(channel))
                  : `${t('email_cp')}`}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      id: 'Rating',
      Header: `${t('responses.filter_condition.response_fields.rating')}`,
      accessor: (row: SurveyResponseType) => {
        const surveyType = surveyInfo?.type;
        const rating = row[`${surveyType?.toLowerCase()}_rating`];
        if (isNil(rating)) return '-';
        const ratingGroup: RangeLabelType =
          row[`${surveyType?.toLowerCase()}_rating_group`]?.toLowerCase();
        const question_type = ratingQuestion?.type_info?.question_type;
        const range = ratingQuestion?.type_info?.scale_properties?.range;
        const isBooleanQuestionType =
          question_type === 'BOOLEAN' ||
          (range?.length === 2 &&
            range?.[0]?.min === 0 &&
            range?.[1].min === 1);
        return (
          <ValueBox label={ratingGroup} rating={rating}>
            {isBooleanQuestionType &&
            ratingQuestion?.type_info?.type_variant &&
            ratingGroup ? (
              <Icon
                width={14}
                height={14}
                fill={
                  ratingQuestion?.type_info?.type_variant === 'EMOJI'
                    ? 'none'
                    : ''
                }
                viewBox="0 0 16 16"
                glyph={getRatingIcon(
                  ratingQuestion?.type_info?.type_variant,
                  ratingGroup,
                )}
              />
            ) : (
              rating
            )}
          </ValueBox>
        );
      },
    },
    {
      id: 'Comment',
      Header: `${t('contacts.import_contact.default_fields.response.comment')}`,
      accessor: ({ comment }: SurveyResponseType) => (
        <>
          {comment ? (
            <div className="comment">
              <Icon
                glyph="comment"
                width={16}
                height={16}
                fill="#2C5CC5"
                viewBox="0 0 14 14"
              />
              <Spacer x={0.3} />
              <Tooltip
                label={comment.replaceAll('<', '&lt;')}
                position="bottom"
              >
                <div className="text">{comment}</div>
              </Tooltip>
            </div>
          ) : (
            '-'
          )}
        </>
      ),
    },
    {
      id: 'Status',
      Header: `${t('contacts.import_contact.default_fields.contact.status')}`,
      accessor: ({ status }: SurveyResponseType) => (
        <>
          {status === 'COMPLETE' ? (
            <Tooltip
              label={`${t('responses.filter_condition.status.complete')}`}
              position="bottom"
            >
              <Icon glyph="tick-complete" width={16} height={16} />
            </Tooltip>
          ) : (
            <Tooltip
              label={`${t('responses.filter_condition.status.partial')}`}
              position="bottom"
            >
              <Icon glyph="tick-partial" width={16} height={16} />
            </Tooltip>
          )}
        </>
      ),
    },
    {
      id: 'Responded on',
      Header: `${t('responses.table_header.responded')}`,
      accessor: ({ created_at }: SurveyResponseType) => {
        const dateTime = moment(created_at)
          .tz(accountInfo?.timezone || moment.tz.guess())
          .format(getAccountTimeFormat(accountInfo));
        return <div>{dateTime}</div>;
      },
    },
  ];

  const onPageClick = (pgNo: number) => {
    setPageIndex(pgNo);
    setAllSelected(false);
  };

  const selectMenuOptions: MenuItemProps[] = [
    {
      id: 'SELECT_PAGE',
      name: `Select ${responseData?.surveyResponses?.length} 
       ${
         responseData?.surveyResponses?.length &&
         responseData?.surveyResponses?.length < 2
           ? 'response'
           : 'responses'
       }`,
      // for now disabling this option if all responses are selected but we may need to enable this if all responses are selected
      disabled:
        (responseData?.surveyResponses?.length != undefined &&
          responseData?.surveyResponses?.length <=
            selectedRowIdsArray.length) ||
        isAllSelected,
    },
    {
      id: 'SELECT_ALL',
      name: `Select all ${totalCount?.count} responses`,
      disabled:
        selectedRowIdsArray?.length == totalCount?.count || isAllSelected,
    },
    {
      id: 'DESELECT_ALL',
      name: 'Deselect all responses',
      disabled: !isAllSelected,
    },
  ];

  const editFilterViewRef: React.MutableRefObject<HTMLDivElement | null> =
    React.useRef<HTMLDivElement>(null);

  if (!currentSurvey && !surveyData?.length && !isDefaultSurveyFetching) {
    return (
      <EmptyState
        title={`${t('empty_state.title.no_responses_here')}`}
        message={`${t(
          'empty_state.message.create_survey_to_receive_response',
        )}`}
        imgSrc="empty-state-survey.svg"
      />
    );
  }

  return (
    <ResponseTable>
      <Table
        disableSelectMenuOptions
        dataKey="surveyserv_response_id"
        data={allresponses || []}
        columns={
          surveyInfo && surveyInfo?.type === 'NONE'
            ? columns.filter((c) => !responseColumnToExclude.includes(c.id))
            : columns
        }
        count={totalCount?.count}
        isLoading={isLoading || isCountLoading || isDefaultSurveyFetching}
        infoComponent={
          showSelectionInfo ? (
            <SelectAllContainer>
              {t('responses.row_options.select_responses', {
                count: isAllSelected ? count : selectedRowIdsArray.length,
              })}
              &nbsp;
              {!isAllSelected && count !== selectedRowIdsArray.length && (
                <FreshLink
                  onClick={() => {
                    setAllSelected(true);
                    setSelectedRowIds((old) => {
                      const newValues = { ...old };
                      (allresponses || []).forEach(
                        (response: SurveyResponseType) => {
                          if (response?.surveyserv_response_id)
                            newValues[response?.surveyserv_response_id] = true;
                        },
                      );
                      return newValues;
                    });
                  }}
                >
                  {t('responses.row_options.all_responses', {
                    count: count,
                  })}
                </FreshLink>
              )}
              {isAllSelected && (
                <>
                  &nbsp;
                  <FreshLink
                    onClick={() => {
                      setAllSelected(false);
                      setSelectedRowIds({});
                    }}
                  >
                    {t('responses.row_options.clear_selection')}
                  </FreshLink>
                </>
              )}
            </SelectAllContainer>
          ) : undefined
        }
        onRowSelect={(row, checked) => {
          if (!checked) setAllSelected(false);
        }}
        selectMenuOptions={selectMenuOptions}
        selectMenuOptionsHandler={(val) => {
          if (val.id === 'SELECT_ALL') setAllSelected(true);
        }}
        selectedRowIds={selectedRowIds}
        setSelectedRowIds={setSelectedRowIds}
        loaderStyles={{ minHeight: 'calc(100vh - 300px)' }}
        pageIndex={pageIndex}
        isFetching={isFetching || isDefaultSurveyFetching}
        pageSize={RESPONSE_PER_PAGE}
        setPageIndex={onPageClick}
        tableOptions={{ autoResetPage: true }}
        disableRowSelect={access.indexOf('response:write') === -1}
        onRowClick={(row) =>
          history.push(
            `/response/${currentSurvey}/${
              (row?.original as SurveyResponseType)?.id
            }`,
            {
              selectedFilter: selectedFilterView,
              surveyResponseCount: totalCount?.count || '0',
            },
          )
        }
        onMenuOptionSelect={(option: string, row: SurveyResponseType) =>
          handleActions(option, row?.id)
        }
        headerComponent={
          <ResponseListHeader
            surveyInfo={surveyInfo || {}}
            surveyResponseCount={totalCount?.count || '0'}
            isLoadingSurveyInfo={isLoadingSurveyInfo || false}
            isLoading={isLoading || isFetching || isCountLoading}
            filterParams={appliedFilters?.filterParams}
            localState={appliedFilters?.localState}
            setSurveyOpen={setSurveyOpen}
            userId={userInfo?.display_id}
            accountInfo={accountInfo}
            isFilterConditionsModified={isFilterConditionsModified}
            filteredViews={
              allFilterViews ? allFilterViews?.filter_view_list : []
            }
            setConfirmationData={setConfirmationData}
            onSave={onSaveFilterView}
            selectedFilter={selectedFilterView as FilterViewType}
            onClearFilterView={clearFilterView}
            selectedRows={isAllSelected ? [] : uniq(selectedRowIdsArray)}
            isAllSelected={isAllSelected}
            setSelectedRowIds={setSelectedRowIds}
            setIsResponsesDeleted={setIsResponsesDeleted}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            editFilterViewRef={editFilterViewRef}
          />
        }
        hideBottomPagination
        hideTableOnEmpty
        filterComponent={
          <FilterContent
            applyFilter={applyFilter}
            handleReset={handleReset}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            accountInfo={accountInfo}
            surveyInfo={surveyInfo || {}}
            listsData={listsData}
          />
        }
        emptyComponent={
          isFetching ? (
            <Center>
              <Loader />
            </Center>
          ) : (
            <EmptyState
              imgSrc="empty-state-response.svg"
              title={`${t('empty_state.title.no_responses_here')}`}
              message={
                appliedFilters?.filterParams !== ''
                  ? `${t('empty_state.message.no_responses_triggers')}`
                  : `${t('empty_state.message.no_responses_in_view')}`
              }
              minHeight="575px"
            />
          )
        }
        rowMenuOptions={(responseData) => {
          if ((access || []).indexOf('response-edit:write') > -1) {
            return [
              {
                id: 'view',
                name: `${t('view')}`,
              },
              {
                id: 'delete',
                name: `${t('delete')}`,
                disabled: responseData?.status === 'PARTIAL',
              },
            ];
          } else
            return [
              {
                id: 'view',
                name: `${t('view')}`,
              },
            ];
        }}
      />

      <Drawer
        isOpen={surveyOpen}
        onDismiss={() => setSurveyOpen(false)}
        isFullHeight
        size="small"
        placement="left"
        hideDismissIcon
        customClass="in-app-drawer"
      >
        <ResponseDrawer
          setCurrentSurvey={setCurrentSurvey}
          toggleDrawer={toggleDrawer}
          currentSurvey={surveyInfo?.id}
          onSelect={onChangeSurvey}
          parent="RESPONSE"
          setAppliedFilters={setAppliedFilters}
          allFilterViews={
            allFilterViews ? allFilterViews?.filter_view_list : []
          }
          selectedFilter={selectedFilterView as FilterViewType}
          setselectedFilter={setselectedFilterView}
          setselectedFilterViewId={setSelectedFilterViewId}
          onFilterViewChange={onDefaultFilterViewChange}
          onFilterViewDelete={onDeleteFilterView}
          setFilterViewName={setFilterViewName}
          setFilterViewEditAnchorEl={setFilterViewEditAnchorEl}
          editFilterViewRef={editFilterViewRef}
        />
      </Drawer>
      <Confirmation
        isOpen={isDeleteOpen}
        onDismiss={() => setIsDeleteOpen(false)}
        title={`${t('confirm_deletion')}`}
        cancelButtonText={`${t('cancel')}`}
        confirmButtonText={`${t('confirm')}`}
        onConfirm={handleDeleteResponse}
      >
        <div style={{ width: '500px' }}>
          {t('forms.confirmation.response_irreversible')}
        </div>
      </Confirmation>
      <FilterViewPopOvers
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        filterViewName={filterViewName}
        setFilterViewName={setFilterViewName}
        saveFilterView={saveFilterView}
        filterViewEditAnchorEl={filterViewEditAnchorEl}
        setFilterViewEditAnchorEl={setFilterViewEditAnchorEl}
      />
    </ResponseTable>
  );
}

export default ResponseList;
